/* ════════ S7.7 Fase 1 — Device Event Timeline (consolidada) ════════
 *
 * Componente ÚNICO para a timeline operacional consolidada do device.
 * Consome GET /api/admin/devices/{id}/timeline (timeline_service): audit,
 * schedule, comandos (SENT/SUCCESS/FAILED) e OTA — unificados, com filtro
 * por categoria e paginação por cursor.
 *
 * NÃO substitui a Timeline existente nesta sprint — é renderizado numa aba
 * própria ("Eventos") para validação. A aba "Timeline" e o /center.timeline
 * seguem inalterados.
 */
import { useCallback, useEffect, useState } from "react";
import { deviceTimeline } from "../lib/api.js";

const CATS = [
  { id: null, label: "Todos" },
  { id: "command", label: "Comandos" },
  { id: "schedule", label: "Agenda" },
  { id: "ota", label: "OTA" },
  { id: "audit", label: "Auditoria" },
  { id: "device", label: "Dispositivo" },  // id "device" intacto (valor da API)
];

const CAT_META = {
  command: { icon: "⎈", color: "#60a5fa" },
  schedule: { icon: "⏱", color: "#a78bfa" },
  ota: { icon: "⬇", color: "#2BD9F0" },
  audit: { icon: "≣", color: "#94a3b8" },
  device: { icon: "🖥️", color: "#35E8A4" },
};

const SEV_COLOR = { info: "#94a3b8", warning: "#FFB224", critical: "#FF5063" };

function fmt(at) {
  if (!at) return "—";
  const d = new Date(at);
  return Number.isNaN(d.getTime())
    ? String(at)
    : d.toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" });
}

export default function DeviceEventTimeline({ deviceId }) {
  const [cat, setCat] = useState(null);
  const [events, setEvents] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  const load = useCallback(async (reset) => {
    if (!deviceId) return;
    setLoading(true); setErr(null);
    try {
      const opts = { kinds: cat || undefined, limit: 30 };
      if (!reset && cursor) opts.before = cursor;
      const r = await deviceTimeline(deviceId, opts);
      const items = r.events || [];
      setEvents((prev) => (reset ? items : [...prev, ...items]));
      setCursor(r.next_cursor || null);
    } catch (e) {
      setErr(e?.message || "Falha ao carregar timeline");
    } finally {
      setLoading(false);
    }
  }, [deviceId, cat, cursor]);

  // recarrega do zero quando muda device ou categoria
  useEffect(() => {
    setEvents([]); setCursor(null);
    let alive = true;
    (async () => {
      if (!deviceId) return;
      setLoading(true); setErr(null);
      try {
        const r = await deviceTimeline(deviceId, { kinds: cat || undefined, limit: 30 });
        if (!alive) return;
        setEvents(r.events || []);
        setCursor(r.next_cursor || null);
      } catch (e) {
        if (alive) setErr(e?.message || "Falha ao carregar timeline");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [deviceId, cat]);

  return (
    <div className="glass-card-pad space-y-3">
      <div className="flex items-center justify-between">
        <div className="font-mono text-[10.5px] font-semibold uppercase tracking-[0.18em] text-noc-muted">
          Eventos (consolidado)
        </div>
        <div className="flex gap-1 flex-wrap">
          {CATS.map((c) => (
            <button
              key={String(c.id)}
              onClick={() => setCat(c.id)}
              className={
                "text-[11px] px-2 py-0.5 rounded border " +
                (cat === c.id
                  ? "border-noc-accent text-noc-accent"
                  : "border-noc-border/50 text-noc-muted hover:text-noc-text")
              }
            >
              {c.label}
            </button>
          ))}
        </div>
      </div>

      {err && <div className="text-xs text-noc-err">{err}</div>}

      {events.length === 0 && !loading && !err && (
        <div className="text-xs text-noc-muted py-2">Sem eventos para o filtro atual.</div>
      )}

      <div className="space-y-1.5">
        {events.map((e, i) => {
          const meta = CAT_META[e.category] || { icon: "·", color: "#94a3b8" };
          const sev = SEV_COLOR[e.severity] || "#94a3b8";
          return (
            <div
              key={(e.source_id || "") + ":" + i}
              className="flex items-start gap-2 text-xs border-b border-noc-border/30 pb-1.5"
            >
              <span style={{ color: meta.color }} className="font-mono">{meta.icon}</span>
              <span className="text-noc-muted font-mono w-[140px] shrink-0">{fmt(e.at)}</span>
              <span className="flex-1">
                <span className="font-medium">{e.title}</span>
                {e.detail && <span className="text-noc-muted"> — {e.detail}</span>}
              </span>
              {e.result && (
                <span style={{ color: sev }} className="font-mono uppercase text-[10px]">
                  {e.result}
                </span>
              )}
            </div>
          );
        })}
      </div>

      <div className="flex items-center justify-between pt-1">
        <span className="text-[11px] text-noc-muted">
          {events.length} evento(s){cursor ? " · há mais" : ""}
        </span>
        {cursor && (
          <button
            onClick={() => load(false)}
            disabled={loading}
            className="text-[11px] px-2 py-0.5 rounded border border-noc-border/50
                       text-noc-muted hover:text-noc-text disabled:opacity-50"
          >
            {loading ? "carregando…" : "carregar mais"}
          </button>
        )}
      </div>
    </div>
  );
}
